import { Contact } from '~api/address_book';

import { useEffect } from 'react';
import { FormProvider, SubmitHandler } from 'react-hook-form';
import { NavLink, useNavigate } from 'react-router-dom';

import { Banner, Breadcrumb, Card, FormControl, LoadingButton, PageTitle, PageWrapper } from '@ingrid/luterkort-react';

import { FormLeaveBlocker } from '~src/components/FormLeaveBlocker/FormLeaveBlocker';
import { MutationBanner } from '~src/components/MutationNotification/MutationNotification';
import { useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';
import { TrackingEventEnum, useTracking } from '~src/main/hooks/useTracking';
import { usePendingShipmentPolling } from '~src/pages/settings/pending-shipment/polling';
import { today } from '~src/utils/internationalized-date';

import { useShipmentCreateMutation } from '../mutations';
import { useShipmentMetadataPolling } from '../polling';
import { ShipmentUpsert, useShipmentForm } from '../types';
import { Addresses } from './addresses';
import { AddressesProvider } from './addresses-context';
import { CarrierContract } from './carrier-contract';
import { CarrierProduct } from './carrier-product';
import { Consignment } from './consignment';
import { CustomsDeclarations } from './customs-declarations';
import { Metadata } from './metadata';
import { References } from './references';
import { Template } from './template';

export interface CreatePageProps {
  onSuccessEvent?: TrackingEventEnum;
  onErrorEvent?: TrackingEventEnum;
  onInitEvent?: TrackingEventEnum;
  shipment?: ShipmentUpsert;
  error?: string;
}

export function CreatePage({ shipment, error, onErrorEvent, onSuccessEvent, onInitEvent }: CreatePageProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const methods = useShipmentForm({
    values: shipment ?? {
      shipping_date: today('UTC').toString(),
    },
  });

  const shipmentMetadataPolling = useShipmentMetadataPolling();
  const pendingShipmentPolling = usePendingShipmentPolling();
  const mutation = useShipmentCreateMutation();
  const tracking = useTracking();
  const navigate = useNavigate();

  useEffect(() => {
    tracking.capture(onInitEvent ?? TrackingEventEnum.ShipmentCreateStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit: SubmitHandler<ShipmentUpsert> = async values => {
    try {
      const { data } = await mutation.mutateAsync(values);

      await shipmentMetadataPolling.mutateAsync({
        // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style
        variables: data.shipment?.id as string,
        pollUntil: data => data?.status === 'CREATED',
      });

      if (values.pending_shipment_id) {
        await pendingShipmentPolling.mutateAsync({
          variables: {
            filtering: {
              id: values.pending_shipment_id,
            },
          },
          pollUntil: data => !data?.length,
        });
      }

      toast({
        variant: 'success',
        title: t('shipments.details.createSuccess'),
      });

      tracking.capture(onSuccessEvent ?? TrackingEventEnum.ShipmentCreateCompleted, { shipmentId: data.shipment?.id });
      navigate('..');
    } catch (e) {
      tracking.capture(onErrorEvent ?? TrackingEventEnum.ShipmentCreateFailed);
      throw e;
    }
  };

  return (
    <PageWrapper gap="lg" variant="center">
      <FormProvider {...methods}>
        <FormControl.Root>
          <FormControl.Form onSubmit={methods.handleSubmit(onSubmit)} style={{ display: 'none' }} />

          <PageTitle.Wrapper>
            <PageTitle.Stack>
              <Breadcrumb aria-label={t('common.breadcrumbs')}>
                <NavLink to="..">{t('common.shipments')}</NavLink>
                <span aria-current="page">{t('common.createShipment')}</span>
              </Breadcrumb>
            </PageTitle.Stack>

            <FormControl.SubmitButton asChild>
              <LoadingButton loading={mutation.isPending || shipmentMetadataPolling.isPending}>
                {t('common.create')}
              </LoadingButton>
            </FormControl.SubmitButton>
          </PageTitle.Wrapper>

          {error && !mutation.isError && <Banner title={t('errors.generic')} subtitle={error} variant="critical" />}
          <MutationBanner mutation={mutation} />

          <AddressesProvider
            contacts={[
              shipment?.contact_from,
              shipment?.contact_to,
              shipment?.contact_return,
              shipment?.customer_info?.contact,
            ].filter((c): c is Contact => !!c)}
          >
            <Card.List>
              <Template />
              <References />
              <Addresses />
              <Consignment />
              <CustomsDeclarations />
              <CarrierProduct />
              <CarrierContract />
              <Metadata />
            </Card.List>
          </AddressesProvider>

          <FormLeaveBlocker formState={methods.formState} />
        </FormControl.Root>
      </FormProvider>
    </PageWrapper>
  );
}
